import { dashboard } from 'types';
import AppConstants from 'constants/AppConstants';

import protos from 'utils/protos';

const { MenuCategory, Menu, MenuSection, MenuItem, MenuModifierGroup, MenuItemModifierGroups } =
  protos;

/**
 * Categories
 */

export function listCategories(): ApiAction {
  return {
    TockApi: {
      getListProto: '/menu/category',
      type: AppConstants.MENU_CATEGORY_FETCH,
    },
  };
}

export function insertCategory(menuCategory: dashboard.IMenuCategory): ApiAction {
  return {
    TockApi: {
      postProto: '/menu/category',
      request: MenuCategory.fromObject(menuCategory),
      type: AppConstants.MENU_CATEGORY_INSERT,
    },
  };
}

export function updateCategory(menuCategory: dashboard.IMenuCategory): ApiAction {
  return {
    TockApi: {
      putProto: '/menu/category',
      request: MenuCategory.fromObject(menuCategory),
      type: AppConstants.MENU_CATEGORY_UPDATE,
    },
  };
}

/**
 * Menus
 */

export function listMenus(): ApiAction {
  return {
    TockApi: {
      getListProto: '/menu',
      type: AppConstants.MENU_FETCH,
    },
  };
}

export function insertMenu(menu: dashboard.IMenu): ApiAction {
  return {
    TockApi: {
      postProto: '/menu',
      request: Menu.fromObject(menu),
      type: AppConstants.MENU_INSERT,
    },
  };
}

export function updateMenu(menu: dashboard.IMenu, toast?: string): ApiAction {
  return {
    TockApi: {
      putProto: '/menu',
      request: Menu.fromObject(menu),
      type: AppConstants.MENU_UPDATE,
      toast,
    },
  };
}

export function deleteMenu(menu: dashboard.IMenu): ApiAction {
  return {
    TockApi: {
      deleteProto: `/menu/${menu.id}`,
      request: Menu.fromObject(menu),
      type: AppConstants.MENU_DELETE,
    },
  };
}

export function duplicateMenu(menuId: number, name: string, toast?: string): ApiAction {
  return {
    TockApi: {
      postProto: `/menu/${menuId}/copy?name=${encodeURIComponent(name)}`,
      type: AppConstants.MENU_COPY,
      toast,
    },
  };
}

/**
 * Menu sections
 */

export function insertMenuSection(menuId: number, menuSection: dashboard.IMenuSection): ApiAction {
  return {
    TockApi: {
      postProto: `/menu/${menuId}/section`,
      request: MenuSection.fromObject(menuSection),
      type: AppConstants.MENU_SECTION_INSERT,
      toast: 'Section added',
    },
  };
}

export function updateMenuSection(menuId: number, menuSection: dashboard.IMenuSection): ApiAction {
  return {
    TockApi: {
      putProto: `/menu/${menuId}/section`,
      request: MenuSection.fromObject(menuSection),
      type: AppConstants.MENU_SECTION_UPDATE,
      toast: 'Menu updated',
    },
  };
}

/**
 * Menu items
 */

export function listMenuItems(): ApiAction {
  return {
    TockApi: {
      getListProto: '/menu/item',
      type: AppConstants.MENU_ITEM_FETCH,
    },
  };
}

export function insertMenuItem(menuItem: dashboard.IMenuItem): ApiAction {
  return {
    TockApi: {
      postProto: '/menu/item',
      request: MenuItem.fromObject(menuItem),
      type: AppConstants.MENU_ITEM_INSERT,
    },
  };
}

export function updateMenuItem(menuItem: dashboard.IMenuItem): ApiAction {
  return {
    TockApi: {
      putProto: '/menu/item',
      request: MenuItem.fromObject(menuItem),
      type: AppConstants.MENU_ITEM_UPDATE,
    },
  };
}

export function deleteMenuItem(menuItem: dashboard.IMenuItem): ApiAction {
  return {
    TockApi: {
      deleteProto: `/menu/item/${menuItem.id}`,
      request: MenuItem.fromObject(menuItem),
      type: AppConstants.MENU_ITEM_DELETE,
    },
  };
}

/**
 * Menu Items <--> Sections
 */

export function insertMenuItemToSection(
  menuItem: dashboard.IMenuItem,
  menuSectionId: number
): ApiAction {
  return {
    TockApi: {
      postProto: `/menu/section/${menuSectionId}/item`,
      request: MenuItem.fromObject(menuItem),
      type: AppConstants.MENU_ITEM_TO_SECTION_INSERT,
    },
  };
}

export function deleteMenuItemToSection(menuItemId: number, menuSectionId: number): ApiAction {
  return {
    TockApi: {
      deleteProto: `/menu/section/${menuSectionId}/item/${menuItemId}`,
      type: AppConstants.MENU_ITEM_TO_SECTION_DELETE,
    },
  };
}

/**
 * Menu modifier groups
 */

export function listMenuModifierGroups(): ApiAction {
  return {
    TockApi: {
      getListProto: '/menu/modifier-group',
      type: AppConstants.MENU_MODIFIER_GROUP_FETCH,
    },
  };
}

export function insertMenuModifierGroup(
  menuModifierGroup: dashboard.IMenuModifierGroup
): ApiAction {
  return {
    TockApi: {
      postProto: '/menu/modifier-group',
      request: MenuModifierGroup.fromObject(menuModifierGroup),
      type: AppConstants.MENU_MODIFIER_GROUP_INSERT,
    },
  };
}

export function updateMenuModifierGroup(
  menuModifierGroup: dashboard.IMenuModifierGroup
): ApiAction {
  return {
    TockApi: {
      putProto: '/menu/modifier-group',
      request: MenuModifierGroup.fromObject(menuModifierGroup),
      type: AppConstants.MENU_MODIFIER_GROUP_UPDATE,
    },
  };
}

export function deleteMenuModifierGroup(
  menuModifierGroup: dashboard.IMenuModifierGroup
): ApiAction {
  return {
    TockApi: {
      deleteProto: `/menu/modifier-group/${menuModifierGroup.id}`,
      request: MenuModifierGroup.fromObject(menuModifierGroup),
      type: AppConstants.MENU_MODIFIER_GROUP_DELETE,
    },
  };
}

/**
 * Menu modifier groups <--> Menu items Many-to-Many
 */
export function listMenuModifierGroupsToMenuItems(
  menuItemIds?: number[],
  menuModifierGroupIds?: number[]
): ApiAction {
  const filterParams = new URLSearchParams();
  if (menuItemIds && menuItemIds.length) {
    filterParams.set('menu_item_ids', menuItemIds.join());
  }
  if (menuModifierGroupIds && menuModifierGroupIds.length) {
    filterParams.set('menu_modifier_group_ids', menuModifierGroupIds.join());
  }

  const paramsStr = filterParams.toString();

  return {
    TockApi: {
      getListProto: `/menu/item/modifier${paramsStr !== '' ? `?${paramsStr}` : ''}`,
      type: AppConstants.MENU_MODIFIER_GROUP_TO_ITEM_FETCH,
    },
  };
}

export function insertMenuModifierGroupToMenuItem(
  modifierGroupId: number,
  menuItemId: number
): ApiAction {
  return {
    TockApi: {
      postProto: `/menu/item/${menuItemId}/modifier/${modifierGroupId}`,
      type: AppConstants.MENU_MODIFIER_GROUP_TO_ITEM_INSERT,
    },
  };
}

export function deleteMenuModifierGroupToMenuItem(
  modifierGroupId: number,
  menuItemId: number
): ApiAction {
  return {
    TockApi: {
      deleteProto: `/menu/item/${menuItemId}/modifier/${modifierGroupId}`,
      type: AppConstants.MENU_MODIFIER_GROUP_TO_ITEM_DELETE,
    },
  };
}

export function updateMenuItemModifierGroups(
  menuItemId: number,
  modifierGroups: dashboard.IMenuModifierGroup[]
): ApiAction {
  const menuModifierGroupsUpdate = MenuItemModifierGroups.fromObject({ modifierGroups });
  return {
    TockApi: {
      putListProto: `/menu/item/${menuItemId}/modifier`,
      request: menuModifierGroupsUpdate,
      type: AppConstants.MENU_ITEM_MODIFIER_GROUPS_UPDATE,
      context: {
        menuItemId,
      },
    },
  };
}
